import { genId } from './types';
import type { FreeTableCell, FreeTableRow, FreeTableSection } from './types';

/**
 * 自由表格的结构运算。
 *
 * 直接在 rowSpan/colSpan 上做增删行列极易算错（合并区跨越插入点、锚点所在行
 * 被删掉等），所以所有操作统一走「炸开 → 改覆盖图 → 合回」：
 *   explode: 表格 → 覆盖图 cover[r][c] = 该格所属合并区的 key
 *   implode: 覆盖图 → 表格（每个区取左上角当锚点，重算 span 与占位格）
 * 结构操作只改 cover / 行列元数据，spans 与 merged 由 implode 一次算对。
 */

export type CellProps = Pick<FreeTableCell, 'content' | 'align' | 'bold' | 'shaded' | 'color' | 'bg' | 'fontSizePx'>;

const DEFAULT_PROPS: CellProps = { content: '', align: 'center', bold: false, shaded: false };

export function makeCell(props: Partial<CellProps> = {}): FreeTableCell {
  return { id: genId(), colSpan: 1, rowSpan: 1, merged: false, ...DEFAULT_PROPS, ...props };
}

function propsOf(cell: FreeTableCell): CellProps {
  return {
    content: cell.content,
    align: cell.align,
    bold: cell.bold,
    shaded: cell.shaded,
    color: cell.color ?? null,
    bg: cell.bg ?? null,
    fontSizePx: cell.fontSizePx ?? null,
  };
}

export interface Rect {
  r0: number;
  c0: number;
  r1: number;
  c1: number;
}

interface Grid {
  /** rows × cols，每格存所属合并区的 key（= 该区锚点单元格 id）。 */
  cover: string[][];
  props: Map<string, CellProps>;
  rowMeta: Array<{ id: string; heightMm: number | null }>;
  colWidths: number[];
}

/** 把宽度数组缩放到总和 100（保留一位小数，余量补到最后一列）。 */
function rescale(widths: number[]): number[] {
  const n = widths.length;
  if (n === 0) return [];
  const clean = widths.map((w) => (Number.isFinite(w) && w > 0 ? w : 1));
  const sum = clean.reduce((a, b) => a + b, 0);
  const out = clean.map((w) => Math.round(((w / sum) * 100 + Number.EPSILON) * 10) / 10);
  const drift = Math.round((100 - out.reduce((a, b) => a + b, 0)) * 10) / 10;
  out[n - 1] = Math.round((out[n - 1] + drift) * 10) / 10;
  return out;
}

function explode(s: FreeTableSection): Grid {
  const C = Math.max(1, s.colWidths.length);
  const R = Math.max(1, s.rows.length);
  const cover: string[][] = Array.from({ length: R }, () => Array<string>(C).fill(''));
  const props = new Map<string, CellProps>();

  for (let r = 0; r < R; r++) {
    for (let c = 0; c < C; c++) {
      const cell = s.rows[r]?.cells[c];
      // 已被别的区占了，或本来就是占位格 —— 跳过（保证每个区都是矩形）
      if (!cell || cell.merged || cover[r][c]) continue;
      props.set(cell.id, propsOf(cell));
      cover[r][c] = cell.id;
      const rs = Math.max(1, Math.trunc(cell.rowSpan) || 1);
      const cs = Math.max(1, Math.trunc(cell.colSpan) || 1);
      for (let dr = 0; dr < rs && r + dr < R; dr++) {
        for (let dc = 0; dc < cs && c + dc < C; dc++) {
          if (!cover[r + dr][c + dc]) cover[r + dr][c + dc] = cell.id;
        }
      }
    }
  }

  // 老数据 / 异常数据里没人认领的格子，各自成一区
  for (let r = 0; r < R; r++) {
    for (let c = 0; c < C; c++) {
      if (cover[r][c]) continue;
      const cell = s.rows[r]?.cells[c];
      const key = cell?.id || genId();
      cover[r][c] = key;
      if (!props.has(key)) props.set(key, cell ? propsOf(cell) : { ...DEFAULT_PROPS });
    }
  }

  const rowMeta = Array.from({ length: R }, (_, r) => ({
    id: s.rows[r]?.id || genId(),
    heightMm: s.rows[r]?.heightMm ?? null,
  }));
  return { cover, props, rowMeta, colWidths: rescale(s.colWidths.slice(0, C)) };
}

/** 每个合并区在覆盖图上的外接矩形。 */
function boxes(cover: string[][]): Map<string, Rect> {
  const map = new Map<string, Rect>();
  cover.forEach((row, r) =>
    row.forEach((key, c) => {
      const b = map.get(key);
      if (!b) map.set(key, { r0: r, c0: c, r1: r, c1: c });
      else {
        b.c0 = Math.min(b.c0, c);
        b.r1 = Math.max(b.r1, r);
        b.c1 = Math.max(b.c1, c);
      }
    }),
  );
  return map;
}

function implode(g: Grid): Pick<FreeTableSection, 'rows' | 'colWidths'> {
  const C = g.colWidths.length;
  const box = boxes(g.cover);
  const rows: FreeTableRow[] = g.rowMeta.map((meta, r) => ({
    id: meta.id,
    heightMm: meta.heightMm,
    cells: Array.from({ length: C }, (_, c) => {
      const key = g.cover[r][c];
      const b = box.get(key)!;
      if (b.r0 !== r || b.c0 !== c) return { ...makeCell(), merged: true };
      return {
        ...makeCell(g.props.get(key) ?? DEFAULT_PROPS),
        id: key,
        colSpan: b.c1 - b.c0 + 1,
        rowSpan: b.r1 - b.r0 + 1,
      };
    }),
  }));
  return { rows, colWidths: g.colWidths };
}

function rebuild(s: FreeTableSection, g: Grid): FreeTableSection {
  return { ...s, ...implode(g) };
}

/** 补齐残缺数据（行列数对不上、span 越界、占位格错位）到自洽状态。 */
export function normalizeFreeTable(s: FreeTableSection): FreeTableSection {
  return rebuild(s, explode(s));
}

export function createFreeTable(rows: number, cols: number): Pick<FreeTableSection, 'colWidths' | 'rows'> {
  const colWidths = rescale(Array<number>(cols).fill(100 / cols));
  return {
    colWidths,
    rows: Array.from({ length: rows }, () => ({
      id: genId(),
      heightMm: null,
      cells: Array.from({ length: cols }, () => makeCell()),
    })),
  };
}

export function rowCount(s: FreeTableSection): number {
  return s.rows.length;
}

export function colCount(s: FreeTableSection): number {
  return s.colWidths.length;
}

/** 在 at 位置插入一行。跨越插入位置的合并区自动拉长一行。 */
export function insertRow(s: FreeTableSection, at: number): FreeTableSection {
  const g = explode(s);
  const R = g.rowMeta.length;
  const idx = Math.max(0, Math.min(at, R));
  const newRow = g.cover[0].map((_, c) => {
    const above = idx > 0 ? g.cover[idx - 1][c] : '';
    const below = idx < R ? g.cover[idx][c] : '';
    if (above && above === below) return above; // 合并区跨过插入线 → 并进去
    const key = genId();
    g.props.set(key, { ...DEFAULT_PROPS });
    return key;
  });
  g.cover.splice(idx, 0, newRow);
  g.rowMeta.splice(idx, 0, { id: genId(), heightMm: null });
  return rebuild(s, g);
}

export function removeRow(s: FreeTableSection, at: number): FreeTableSection {
  const g = explode(s);
  if (g.rowMeta.length <= 1 || at < 0 || at >= g.rowMeta.length) return s;
  g.cover.splice(at, 1);
  g.rowMeta.splice(at, 1);
  return rebuild(s, g);
}

/** 在 at 位置插入一列。跨越插入位置的合并区自动拉宽一列。 */
export function insertCol(s: FreeTableSection, at: number): FreeTableSection {
  const g = explode(s);
  const C = g.colWidths.length;
  const idx = Math.max(0, Math.min(at, C));
  for (const row of g.cover) {
    const left = idx > 0 ? row[idx - 1] : '';
    const right = idx < C ? row[idx] : '';
    if (left && left === right) {
      row.splice(idx, 0, left);
    } else {
      const key = genId();
      g.props.set(key, { ...DEFAULT_PROPS });
      row.splice(idx, 0, key);
    }
  }
  g.colWidths.splice(idx, 0, 100 / (C + 1));
  g.colWidths = rescale(g.colWidths);
  return rebuild(s, g);
}

export function removeCol(s: FreeTableSection, at: number): FreeTableSection {
  const g = explode(s);
  if (g.colWidths.length <= 1 || at < 0 || at >= g.colWidths.length) return s;
  for (const row of g.cover) row.splice(at, 1);
  g.colWidths.splice(at, 1);
  g.colWidths = rescale(g.colWidths);
  return rebuild(s, g);
}

export function setColWidth(s: FreeTableSection, col: number, percent: number): FreeTableSection {
  const C = s.colWidths.length;
  if (C <= 1 || col < 0 || col >= C) return s;
  const next = Math.max(2, Math.min(90, percent));
  const rest = 100 - next;
  const others = s.colWidths.filter((_, i) => i !== col);
  const sumOthers = others.reduce((a, b) => a + b, 0) || 1;
  const widths = s.colWidths.map((w, i) => (i === col ? next : (w / sumOthers) * rest));
  return { ...s, colWidths: rescale(widths) };
}

export function setRowHeight(s: FreeTableSection, row: number, heightMm: number | null): FreeTableSection {
  if (row < 0 || row >= s.rows.length) return s;
  return { ...s, rows: s.rows.map((r, i) => (i === row ? { ...r, heightMm } : r)) };
}

/** 选区所在的合并区外接矩形（点到占位格时返回它所属区的完整矩形）。 */
export function regionRect(s: FreeTableSection, r: number, c: number): Rect {
  const g = explode(s);
  const key = g.cover[r]?.[c];
  const b = key ? boxes(g.cover).get(key) : undefined;
  return b ?? { r0: r, c0: c, r1: r, c1: c };
}

/** 把矩形撑到不切断任何合并区（否则会做出非矩形的格子）。 */
function expand(cover: string[][], rect: Rect): Rect {
  const box = boxes(cover);
  const out = { ...rect };
  for (;;) {
    let grew = false;
    for (let r = out.r0; r <= out.r1; r++) {
      for (let c = out.c0; c <= out.c1; c++) {
        const b = box.get(cover[r][c]);
        if (!b) continue;
        if (b.r0 < out.r0) { out.r0 = b.r0; grew = true; }
        if (b.c0 < out.c0) { out.c0 = b.c0; grew = true; }
        if (b.r1 > out.r1) { out.r1 = b.r1; grew = true; }
        if (b.c1 > out.c1) { out.c1 = b.c1; grew = true; }
      }
    }
    if (!grew) return out;
  }
}

/** 合并选区。内容保留左上角那格的，其余丢弃。 */
export function mergeRange(s: FreeTableSection, rect: Rect): FreeTableSection {
  const g = explode(s);
  const R = g.rowMeta.length;
  const C = g.colWidths.length;
  const clamped: Rect = {
    r0: Math.max(0, Math.min(rect.r0, rect.r1)),
    c0: Math.max(0, Math.min(rect.c0, rect.c1)),
    r1: Math.min(R - 1, Math.max(rect.r0, rect.r1)),
    c1: Math.min(C - 1, Math.max(rect.c0, rect.c1)),
  };
  const box = expand(g.cover, clamped);
  if (box.r0 === box.r1 && box.c0 === box.c1) return s;
  const key = g.cover[box.r0][box.c0];
  for (let r = box.r0; r <= box.r1; r++) {
    for (let c = box.c0; c <= box.c1; c++) g.cover[r][c] = key;
  }
  return rebuild(s, g);
}

/** 拆分：把 (r,c) 所在合并区还原成单格，内容留在左上角。 */
export function splitCell(s: FreeTableSection, r: number, c: number): FreeTableSection {
  const g = explode(s);
  const key = g.cover[r]?.[c];
  if (!key) return s;
  const b = boxes(g.cover).get(key);
  if (!b || (b.r0 === b.r1 && b.c0 === b.c1)) return s;
  for (let rr = b.r0; rr <= b.r1; rr++) {
    for (let cc = b.c0; cc <= b.c1; cc++) {
      if (rr === b.r0 && cc === b.c0) continue;
      const nk = genId();
      g.props.set(nk, { ...DEFAULT_PROPS });
      g.cover[rr][cc] = nk;
    }
  }
  return rebuild(s, g);
}

/** 批量改选区内所有锚点格的样式/内容。 */
export function patchCells(
  s: FreeTableSection,
  rect: Rect,
  patch: Partial<CellProps>,
): FreeTableSection {
  const r0 = Math.min(rect.r0, rect.r1);
  const r1 = Math.max(rect.r0, rect.r1);
  const c0 = Math.min(rect.c0, rect.c1);
  const c1 = Math.max(rect.c0, rect.c1);
  return {
    ...s,
    rows: s.rows.map((row, r) => {
      if (r < r0 || r > r1) return row;
      return {
        ...row,
        cells: row.cells.map((cell, c) =>
          c >= c0 && c <= c1 && !cell.merged ? { ...cell, ...patch } : cell,
        ),
      };
    }),
  };
}

/** (r,c) 处真正负责渲染的锚点格（点到占位格时上溯）。 */
export function anchorAt(s: FreeTableSection, r: number, c: number): FreeTableCell | null {
  const cell = s.rows[r]?.cells[c];
  if (!cell) return null;
  if (!cell.merged) return cell;
  const b = regionRect(s, r, c);
  return s.rows[b.r0]?.cells[b.c0] ?? null;
}
